import { useTranslation } from '@spcc/core';
import { cn, Badge, Button } from '@spcc/uikit';
import type {
  GateConfig,
  GateSide,
  GateStatus,
  WeighStage,
} from '../../domain/types';

interface GateControlCardProps {
  stage: WeighStage;
  side: GateSide;
  gate: GateConfig | undefined;
  status: GateStatus;
  canControl: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  className?: string;
}

const SIDE_COLORS: Record<
  GateSide,
  { open: string; closed: string; badge: string }
> = {
  entry: {
    open: 'border-green-400 bg-green-50/60 shadow-[0_0_15px_rgba(74,222,128,0.15)] dark:bg-green-900/15',
    closed: 'border-border/60 bg-card shadow-sm hover:shadow-md',
    badge:
      'bg-green-100 text-green-700 ring-1 ring-inset ring-green-600/20 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-500/20',
  },
  exit: {
    open: 'border-orange-400 bg-orange-50/60 shadow-[0_0_15px_rgba(251,146,60,0.15)] dark:bg-orange-900/15',
    closed: 'border-border/60 bg-card shadow-sm hover:shadow-md',
    badge:
      'bg-orange-100 text-orange-700 ring-1 ring-inset ring-orange-600/20 dark:bg-orange-900/30 dark:text-orange-300 dark:ring-orange-500/20',
  },
};

function StatusDot({ status }: { status: GateStatus }) {
  return (
    <span
      className={cn(
        'inline-flex h-2.5 w-2.5 shrink-0 rounded-full',
        status === 'open'
          ? 'bg-green-500 shadow-[0_0_6px_2px_rgba(34,197,94,0.4)] animate-pulse'
          : 'bg-slate-300 ring-1 ring-inset ring-black/10 dark:bg-slate-600 dark:ring-white/10'
      )}
    />
  );
}

export function GateControlCard({
  stage,
  side,
  gate,
  status,
  canControl,
  onOpen,
  onClose,
  className,
}: GateControlCardProps) {
  const { t } = useTranslation();
  const colors = SIDE_COLORS[side];
  const isOpen = status === 'open';
  const stageKey = stage === 'first_weight' ? 'firstWeight' : 'secondWeight';
  const sideKey = side === 'entry' ? 'entry' : 'exit';

  const sideLabel = t(`weighbridgeOps.gate.${sideKey}`);
  const statusLabel = isOpen
    ? t('weighbridgeOps.gate.open')
    : t('weighbridgeOps.gate.closed');
  const openAriaLabel = `${t('weighbridgeOps.gate.openAria')} ${sideLabel} ${t(
    `weighbridgeOps.stage.${stageKey}`
  )}`;
  const closeAriaLabel = `${t(
    'weighbridgeOps.gate.closeAria'
  )} ${sideLabel} ${t(`weighbridgeOps.stage.${stageKey}`)}`;

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-xl border p-4 transition-colors duration-200 ease-in-out',
        isOpen ? colors.open : colors.closed,
        className
      )}
    >
      {/* Header row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <StatusDot status={status} />
          <span className="text-[13px] font-bold uppercase tracking-wider text-foreground">
            {sideLabel}
          </span>
        </div>
        <Badge
          variant="secondary"
          className={cn(
            'px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest transition-colors',
            isOpen
              ? colors.badge
              : 'bg-muted text-muted-foreground ring-1 ring-inset ring-border'
          )}
        >
          {statusLabel}
        </Badge>
      </div>

      {/* Gate info */}
      <div className="flex min-h-[1.5rem] items-center gap-2">
        {gate ? (
          <>
            <span className="rounded bg-muted/50 px-1.5 py-0.5 font-mono text-[10px] font-medium text-muted-foreground ring-1 ring-inset ring-border/50">
              {gate.ip}
            </span>
            {gate.code && (
              <span className="rounded bg-muted/50 px-1.5 py-0.5 font-mono text-[10px] font-medium text-muted-foreground ring-1 ring-inset ring-border/50">
                {gate.code}
              </span>
            )}
          </>
        ) : (
          <span className="text-[11px] font-medium italic text-muted-foreground/70">
            {t('weighbridgeOps.gate.notConfigured')}
          </span>
        )}
      </div>

      {/* Controls */}
      {canControl && gate && (
        <div className="flex gap-2 pt-1.5">
          <Button
            size="sm"
            variant={isOpen ? 'outline' : 'default'}
            className={cn(
              'h-9 flex-1 text-xs font-bold transition-all',
              !isOpen
                ? 'bg-green-600 text-white shadow-sm hover:bg-green-700 hover:shadow active:scale-[0.98]'
                : 'bg-background hover:bg-muted'
            )}
            disabled={isOpen}
            onClick={onOpen}
            aria-label={openAriaLabel}
          >
            {t('weighbridgeOps.gate.openBtn')}
          </Button>
          <Button
            size="sm"
            variant={!isOpen ? 'outline' : 'default'}
            className={cn(
              'h-9 flex-1 text-xs font-bold transition-all',
              isOpen
                ? 'bg-red-600 text-white shadow-sm hover:bg-red-700 hover:shadow active:scale-[0.98]'
                : 'bg-background hover:bg-muted'
            )}
            disabled={!isOpen}
            onClick={onClose}
            aria-label={closeAriaLabel}
          >
            {t('weighbridgeOps.gate.closeBtn')}
          </Button>
        </div>
      )}
    </div>
  );
}
